import { HTTP_TERMINOLOGY_HL7_ORG } from '@medplum/core';
import type { CodeableConcept, Consent } from '@medplum/fhirtypes';
import { SMS_CONSENT_POLICY_SYSTEM } from '../notifications/preferences';

// ─── Code systems used by Consent codings ────────────────────────────────────

export const CONSENT_SCOPE_SYSTEM = `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentscope`;
export const CONSENT_CATEGORY_SYSTEM = `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/v3-ActCode`;
export const CONSENT_POLICY_SYSTEM = `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentpolicycodes`;
export const POLARIS_POLICY_SYSTEM = 'http://polarissleep.com/fhir/CodeSystem/consentpolicycodes';
export const CONTACT_CHANNEL_SYSTEM = 'http://polarissleep.com/fhir/CodeSystem/contact-channel';

// ─── Reusable CodeableConcept fragments ──────────────────────────────────────

export const consentScopeMapping = {
  patientPrivacy: {
    coding: [{ system: CONSENT_SCOPE_SYSTEM, code: 'patient-privacy', display: 'Patient Privacy' }],
  },
  treatment: {
    coding: [{ system: CONSENT_SCOPE_SYSTEM, code: 'treatment', display: 'Treatment' }],
  },
} as const satisfies Record<string, CodeableConcept>;

export const consentCategoryMapping = {
  nopp: {
    coding: [{ system: CONSENT_CATEGORY_SYSTEM, code: 'nopp', display: 'Notice of Privacy Practices' }],
  },
  med: {
    coding: [{ system: CONSENT_CATEGORY_SYSTEM, code: 'med', display: 'Medical' }],
  },
} as const satisfies Record<string, CodeableConcept>;

export const consentPolicyRuleMapping = {
  hipaaNpp: {
    coding: [{ system: CONSENT_POLICY_SYSTEM, code: 'hipaa-npp', display: 'HIPAA Notice of Privacy Practices' }],
  },
  cric: {
    coding: [{ system: CONSENT_POLICY_SYSTEM, code: 'cric', display: 'Common Rule Informed Consent' }],
  },
  marketing: {
    coding: [{ system: POLARIS_POLICY_SYSTEM, code: 'marketing-consent', display: 'Consent to Marketing' }],
  },
  tcpaSms: {
    coding: [
      { system: SMS_CONSENT_POLICY_SYSTEM, code: 'tcpa-sms', display: 'TCPA SMS Messaging Consent (A2P 10DLC)' },
    ],
  },
} as const satisfies Record<string, CodeableConcept>;

// ─── Definitions ─────────────────────────────────────────────────────────────

export interface ConsentDefinition {
  key: string;
  title: string;
  description: string;
  /** Patient-facing copy explaining the consequence of revoking the consent. */
  revokeWarning: string;
  scope: CodeableConcept;
  category: CodeableConcept;
  policyRule: CodeableConcept;
  /** Required consents block onboarding completion + surface as tasks. */
  required: boolean;
}

export const FEATURED_CONSENT_KEY = 'consent-to-contact';
export const SMS_CONSENT_KEY = 'sms-consent';

export const CONSENT_DEFINITIONS: ConsentDefinition[] = [
  {
    key: 'consent-to-contact',
    title: 'Consent to Contact',
    description:
      'This allows us to contact you via phone, email, and text about your appointments, test results, and updates to your care.',
    revokeWarning:
      'All messaging from Polaris Sleep will be disabled, including: appointment reminders, updates to your chart, support messages, and prescription reminders.',
    scope: consentScopeMapping.patientPrivacy,
    category: consentCategoryMapping.nopp,
    policyRule: consentPolicyRuleMapping.cric,
    required: true,
  },
  {
    key: 'consent-to-market',
    title: 'Consent to Market',
    description:
      'This allows us to send you communications beyond care updates, including notifications of new services and more.',
    revokeWarning:
      'You will no longer receive marketing communications from Polaris Sleep, including notifications of new services and promotions.',
    scope: consentScopeMapping.patientPrivacy,
    category: consentCategoryMapping.nopp,
    policyRule: consentPolicyRuleMapping.marketing,
    required: false,
  },
  {
    key: 'informed-consent',
    title: 'Informed Consent',
    description:
      'This allows us to treat you. This agreement says that you understand the benefits and risks associated with receiving testing and treatment from Polaris Sleep.',
    revokeWarning:
      'Polaris Sleep will no longer be authorized to provide you with testing and treatment services. Your existing care plan may be affected.',
    scope: consentScopeMapping.treatment,
    category: consentCategoryMapping.med,
    policyRule: consentPolicyRuleMapping.cric,
    required: true,
  },
  // {
  //   key: 'release-of-information',
  //   title: 'Release of Information',
  //   description:
  //     'This allows us to share your medical information with other providers and refer you to other sources of care if needed.',
  //   revokeWarning:
  //     'Polaris Sleep will no longer be authorized to share your medical information with other providers or refer you to other sources of care.',
  //   scope: consentScopeMapping.patientPrivacy,
  //   category: consentCategoryMapping.nopp,
  //   policyRule: consentPolicyRuleMapping.hipaaNpp,
  //   required: true,
  // },
  {
    key: 'sms-consent',
    title: 'SMS Text Message Consent',
    description:
      'This allows us to send you text messages (SMS) about appointments, test results, and care updates. Message and data rates may apply. Message frequency varies. Reply STOP to opt out. Reply HELP for help.',
    revokeWarning:
      'You will no longer receive SMS text messages from Polaris Sleep, including appointment reminders. You can re-enable SMS at any time from this page.',
    scope: consentScopeMapping.patientPrivacy,
    category: consentCategoryMapping.nopp,
    policyRule: consentPolicyRuleMapping.tcpaSms,
    required: false,
  },
];

// ─── Contact-channel preferences (stored on consent-to-contact Consent) ──────

export interface ContactChannelDefinition {
  /** Channel code stored under CONTACT_CHANNEL_SYSTEM in Consent.provision.action. */
  code: string;
  /** Patient-facing label. */
  label: string;
}

export const CONTACT_CHANNEL_PHONE = 'phone';
export const CONTACT_CHANNEL_EMAIL = 'email';

/**
 * Channels persisted to `consent-to-contact` Consent.provision.action.
 *
 * SMS preference is intentionally NOT in this list — it lives on the separate
 * `sms-consent` resource (TCPA / A2P 10DLC), which has its own status. The
 * provider UI surfaces an SMS toggle alongside these channels but routes it to
 * the dedicated Consent so patient-app + provider-app agree on a single source.
 */
export const CONTACT_CHANNELS: ContactChannelDefinition[] = [
  { code: CONTACT_CHANNEL_PHONE, label: 'Phone Call' },
  { code: CONTACT_CHANNEL_EMAIL, label: 'Email' },
];

// ─── Generic helpers ─────────────────────────────────────────────────────────

function codingMatches(a: CodeableConcept | undefined, b: CodeableConcept | undefined): boolean {
  const codeA = a?.coding?.[0]?.code;
  const codeB = b?.coding?.[0]?.code;
  return Boolean(codeA && codeB && codeA === codeB);
}

export function findConsentForDefinition(consents: Consent[], definition: ConsentDefinition): Consent | undefined {
  return consents.find(
    (c) =>
      codingMatches(c.scope, definition.scope) &&
      codingMatches(c.category?.[0], definition.category) &&
      codingMatches(c.policyRule, definition.policyRule)
  );
}

export function isConsentActive(consent: Consent | undefined): boolean {
  return consent?.status === 'active';
}

export function countMissingRequiredConsents(consents: Consent[]): number {
  return CONSENT_DEFINITIONS.filter((def) => def.required && !isConsentActive(findConsentForDefinition(consents, def)))
    .length;
}

// ─── Contact-channel helpers ─────────────────────────────────────────────────

/**
 * Extract the set of contact-channel codes recorded under
 * `CONTACT_CHANNEL_SYSTEM` in a consent's provision.action.
 *
 * Returns an empty set when the consent is missing, has no provision, or has
 * provision entries from other systems only.
 */
export function getConsentChannels(consent: Consent | undefined): Set<string> {
  const codes = new Set<string>();
  for (const action of consent?.provision?.action ?? []) {
    for (const c of action.coding ?? []) {
      if (c.system === CONTACT_CHANNEL_SYSTEM && c.code) {
        codes.add(c.code);
      }
    }
  }
  return codes;
}

/**
 * Replace the CONTACT_CHANNEL_SYSTEM entries in `consent.provision.action` with
 * exactly the codes in `codes`, preserving any other provision.action entries
 * (other systems) untouched.
 */
export function withChannelsOnConsent(consent: Consent, codes: Set<string>): Consent {
  const otherActions = (consent.provision?.action ?? []).filter(
    (a) => !(a.coding ?? []).some((c) => c.system === CONTACT_CHANNEL_SYSTEM)
  );
  const channelActions = Array.from(codes).map((code) => ({
    coding: [{ system: CONTACT_CHANNEL_SYSTEM, code }],
  }));
  return {
    ...consent,
    provision: {
      ...(consent.provision ?? {}),
      action: [...otherActions, ...channelActions],
    },
  };
}

/**
 * Whether a channel is allowed for dispatch.
 *
 * Treats *absent or empty* provision.action as "no preference recorded" → allow,
 * preserving behaviour for patients onboarded before contact-channel selection
 * existed. Once any channel is explicitly recorded, the list becomes an
 * allow-list and missing channels are denied.
 */
export function isChannelAllowed(consent: Consent | undefined, channel: string): boolean {
  const channels = getConsentChannels(consent);
  if (channels.size === 0) {
    return true;
  }
  return channels.has(channel);
}
